<script setup>
/**
 * AgentShoppingActions.vue  (agent chat-window shopping actions - Tyler 2026-07-01)
 *
 * Three agent write-actions on a product the customer is viewing/has viewed, calling the token-gated
 * Nextlevel_ChatContext agent endpoints (/chatcontext/agent/{setcatalogprice,createcoupon,addproduct}):
 *   1. Set store-wide price (#52) - preview -> typed-confirm modal (blast-radius in words,
 *      old -> new, margin status) -> apply at GLOBAL scope -> one-click exact REVERT.
 *      Hidden when the server capability read (allowed_writes.setcatalogprice) is false;
 *      the BFF + Magento role-gate stays the real enforcement.
 *   1b. Reprice from source (#53) - ASYNC: run (enqueue a PriceCapture scrape of the product's
 *      source_url, get job_id) -> poll status every ~2.5s (HARD CAP ~60s, then a Retry - never
 *      hang) -> ready preview (current -> scraped price, margin, blast radius) -> typed-confirm
 *      write (server re-reads the scraped price; shared CatalogPriceWriter). scrape_failed is
 *      FAIL-CLOSED (no write offered, never a guessed price). REVERT reuses the #52
 *      setcatalogprice revert=1 endpoint via the confirm response's revert_hint. Hidden when
 *      allowed_writes.repricefromsource is false; the BFF + Magento role-gate stays the real
 *      enforcement (role-gate applies to confirm only; run/status are read-only).
 *   2. Make coupon         - margin-safe, per-customer (email-scoped); over-floor auto-routes to approval.
 *   3. Add product from URL - scrape -> create (reuses the UrlProductAdd pipeline).
 *   4. Add to cart (#55) - LOWER-risk (one customer's own reversible cart, not store-wide,
 *      not financial): LIGHT confirm (preview -> add), NO typed-confirm, NO role-gate.
 *      Prefill from data.behavior.current_product ("Add the part they're viewing"), or the
 *      REUSED cockpit partsearch (BFF partsearch read) to pick a product; qty (default 1);
 *      preview shows what will be added + the current cart; add shows the added line + the
 *      cart's new item_count/subtotal + a Remove undo (the removecartline companion action).
 *      The customer's quote is resolved SERVER-SIDE by Magento from the conversation
 *      contact's email (BFF-injected) - the agent can never target another customer's cart.
 *      Hidden when allowed_writes.addtocart is false (absent map = show, same pattern).
 * Auth: X-Agent-Token (cfg.overrideToken) + email, origin-pinned server-side.
 */
import { ref, computed, onBeforeUnmount } from 'vue';
import { useInteliBff } from 'dashboard/intelichat/composables/useInteliBff';

const props = defineProps({
  baseUrl: { type: String, default: '' },
  agentToken: { type: String, default: '' },
  email: { type: String, default: '' },
  conversationId: { type: [String, Number], default: '' },
  products: { type: Array, default: () => [] }, // [{sku,name,price}]
  // #52: intel data.behavior (current_product = the single most-recent product_view,
  // fallback repeat_views[0]) - the store-wide price prefill source.
  behavior: { type: Object, default: null },
  // #52: intel data.allowed_writes {action: bool} - server-computed capability map for
  // the AUTHENTICATED agent (role resolved server-side; never client-supplied).
  allowedWrites: { type: Object, default: null },
});

const open = ref('');
const toggle = k => { open.value = open.value === k ? '' : k; };
// Actions render when the agent context (baseUrl + token) is present. Email is required only for the
// customer-scoped coupon action, which validates it at click-time; price/add-product do not need it.
// Render when we have a conversation. Auth is the Chatwoot session (BFF); no browser token needed.
const ready = computed(() => !!props.conversationId);

// BFF write (Option B): same-origin, session-authed via window.axios. Server resolves email + holds the
// Magento write token; the browser sends NO token. idempotency_key dedupes double-applies server-side.
const routeAccountId = () => String((window.location.pathname.match(/\/accounts\/(\d+)/) || [])[1] || '');
const mkIdem = () => 'nlbff-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
const post = async (action, params, idem) => {
  const aid = routeAccountId();
  const payload = { proxy_action: action, conversation_id: String(props.conversationId || ''), ...params };
  if (idem) payload.idempotency_key = idem;
  const { data } = await window.axios.post(`/api/v1/accounts/${aid}/nextlevel/agent/write`, payload);
  return data;
};

// ---- 1. store-wide price (#52) ----
// Preview (no confirm) -> typed-confirm modal -> apply at scope=global -> one-click REVERT
// (revert=1 restores the exact prior price from the server-side audit ledger).
// Prefill: the browsed product from intel data.behavior.current_product, fallback repeat_views[0].
const currentProduct = computed(() => {
  const b = props.behavior || {};
  const cp = b.current_product;
  if (cp && (cp.product_id || cp.sku)) return cp;
  const rv = Array.isArray(b.repeat_views) ? b.repeat_views[0] : null;
  return rv && (rv.product_id || rv.sku) ? rv : null;
});
// UX-hide only: the button disappears when the server capability read says this agent may
// not setcatalogprice (allowed_writes.setcatalogprice === false). Absent map = show; the
// BFF 403 (ROLE_NOT_ALLOWED) + the Magento role-gate are the real enforcement.
const priceAllowed = computed(() => {
  const aw = props.allowedWrites;
  if (!aw || typeof aw !== 'object') return true;
  return aw.setcatalogprice !== false;
});
const priceSku = ref(''); // '' = use the prefilled browsed product; select overrides
const priceTarget = computed(() => {
  if (priceSku.value) {
    const p = (props.products || []).find(x => x.sku === priceSku.value);
    return p ? { sku: p.sku, name: p.name, current_price: p.price } : { sku: priceSku.value };
  }
  return currentProduct.value;
});
const newPrice = ref('');
const pricePreview = ref(null);   // preview payload {current_price, new_price, delta_pct, margin_floor, margin_ok, ...}
const priceApplied = ref(null);   // applied payload (current_price = the PRIOR price -> the revert offer)
const priceReverted = ref(null);  // revert payload
const typedConfirm = ref('');
const overrideAck = ref(false);   // second acknowledgement for a below-floor apply
const needsOverride = ref(false); // set by a 422 (below margin floor)
const showPriceModal = ref(false);
const priceMsg = ref('');
const priceBusy = ref(false);

const modalSku = computed(() => String(priceTarget.value?.sku || ''));
const modalName = computed(() => priceTarget.value?.name || modalSku.value || 'product');
// product_id direct when we have it (no SKU->id round-trip), else sku
const priceIdFields = () => {
  const t = priceTarget.value || {};
  return t.product_id ? { product_id: String(t.product_id) } : { sku: String(t.sku || '') };
};
const typedOk = computed(() => {
  const t = typedConfirm.value.trim();
  if (!t) return false;
  return t === 'CONFIRM' || (modalSku.value && t.toLowerCase() === modalSku.value.toLowerCase());
});
const marginBlocked = computed(() => pricePreview.value?.margin_ok === false || needsOverride.value);
const canApplyPrice = computed(() => typedOk.value && (!marginBlocked.value || overrideAck.value));

const priceErrText = e => {
  const st = e?.response?.status;
  const body = e?.response?.data || {};
  if (st === 403) return 'Not allowed: this agent account has no store-wide pricing role.';
  if (st === 422) {
    needsOverride.value = true;
    const floor = body.margin_floor != null ? ` ($${body.margin_floor})` : '';
    return `Below the margin floor${floor}. Check the margin override acknowledgement to apply anyway.`;
  }
  if (st === 409) return 'Already changed by another agent (duplicate request). Refresh and re-check before retrying.';
  if (body.error === 'no_prior_value_to_revert') return 'Nothing to revert: no prior price is recorded for this product.';
  return body.error || 'Request failed.';
};

const resetPriceFlow = () => {
  pricePreview.value = null; priceApplied.value = null; priceReverted.value = null;
  typedConfirm.value = ''; overrideAck.value = false; needsOverride.value = false; priceMsg.value = '';
};

const previewPrice = async () => {
  priceMsg.value = '';
  if (!priceTarget.value || !(parseFloat(newPrice.value) > 0)) { priceMsg.value = 'Pick a product and enter a price.'; return; }
  resetPriceFlow();
  priceBusy.value = true;
  try {
    // PREVIEW form: no confirm at all -> a pure read (mutates nothing, passes the role-gate)
    const r = await post('setcatalogprice', { ...priceIdFields(), new_price: newPrice.value, scope: 'global' });
    if (r.ok && r.preview) { pricePreview.value = r; showPriceModal.value = true; }
    else priceMsg.value = r.error || 'preview failed';
  } catch (e) { priceMsg.value = priceErrText(e); }
  finally { priceBusy.value = false; }
};

const applyStorewide = async () => {
  if (!canApplyPrice.value || priceBusy.value) return;
  priceBusy.value = true; priceMsg.value = '';
  try {
    // GLOBAL write: confirm carries the TYPED value (the SKU or the literal CONFIRM)
    const r = await post('setcatalogprice', {
      ...priceIdFields(), new_price: newPrice.value, scope: 'global',
      confirm: typedConfirm.value.trim(), margin_override: overrideAck.value ? '1' : '0',
    }, mkIdem());
    if (r.ok && r.applied) priceApplied.value = r;
    else if (r.ok && r.preview) priceMsg.value = 'Server treated this as a preview. Type the exact SKU or CONFIRM and apply again.';
    else priceMsg.value = r.error || 'apply failed';
  } catch (e) { priceMsg.value = priceErrText(e); }
  finally { priceBusy.value = false; }
};

// One-click exact REVERT (lock #5): same endpoint, revert=1 + typed confirm; the server
// restores the exact prior price from the nlh_agent_cart_audit ledger (no token needed).
const revertStorewide = async () => {
  if (priceBusy.value) return;
  priceBusy.value = true; priceMsg.value = '';
  try {
    const r = await post('setcatalogprice', {
      ...priceIdFields(), revert: '1', scope: 'global',
      confirm: typedOk.value ? typedConfirm.value.trim() : 'CONFIRM',
    }, mkIdem());
    if (r.ok) priceReverted.value = r;
    else priceMsg.value = r.error || 'revert failed';
  } catch (e) { priceMsg.value = priceErrText(e); }
  finally { priceBusy.value = false; }
};

const closePriceModal = () => {
  showPriceModal.value = false;
  if (priceApplied.value) { newPrice.value = ''; resetPriceFlow(); }
};

// ---- 1b. reprice from source (#53) ----
// ASYNC flow: run (enqueue) -> poll status (2.5s interval, HARD CAP ~60s) -> ready preview
// -> typed-confirm write. The scraped price is re-read SERVER-SIDE from the completed job;
// the client never supplies a price. scrape_failed = fail-closed, no write offered.
// UX-hide only (same pattern as #52): absent map = show; the BFF ROLE_NOT_ALLOWED 403 +
// the Magento role-gate (confirm phase only) are the real enforcement.
const repriceAllowed = computed(() => {
  const aw = props.allowedWrites;
  if (!aw || typeof aw !== 'object') return true;
  return aw.repricefromsource !== false;
});
const rpSku = ref(''); // '' = use the prefilled browsed product (current_product); select overrides
const rpTarget = computed(() => {
  if (rpSku.value) {
    const p = (props.products || []).find(x => x.sku === rpSku.value);
    return p ? { sku: p.sku, name: p.name, current_price: p.price } : { sku: rpSku.value };
  }
  return currentProduct.value; // #53 prefill = data.behavior.current_product, fallback repeat_views[0]
});
const rpIdFields = () => {
  const t = rpTarget.value || {};
  return t.product_id ? { product_id: String(t.product_id) } : { sku: String(t.sku || '') };
};
const rpState = ref('idle'); // idle | queueing | polling | timeout | ready | scrape_failed | applied
const rpJobId = ref(null);
const rpPreview = ref(null);  // status=ready payload {current_price, scraped_price, delta_pct, margin_*, blast_radius, ...}
const rpApplied = ref(null);  // confirm payload {before, after, revert_hint}
const rpReverted = ref(null);
const rpMsg = ref('');
const rpBusy = ref(false);
const rpTyped = ref('');
const rpOverrideAck = ref(false);
const rpNeedsOverride = ref(false); // set by a 422 (below margin floor)
const RP_POLL_MS = 2500;
const RP_POLL_CAP = 24; // ~60s hard cap - degrade to Retry, never hang the agent
let rpTimer = null;
let rpTries = 0;
const rpStopPoll = () => { if (rpTimer) { clearInterval(rpTimer); rpTimer = null; } };
onBeforeUnmount(rpStopPoll);

const rpErrText = e => {
  const st = e?.response?.status;
  const body = e?.response?.data || {};
  if (st === 403) return 'Not allowed: this agent account has no repricing role.';
  if (st === 422) {
    rpNeedsOverride.value = true;
    const floor = body.margin_floor != null ? ` ($${body.margin_floor})` : '';
    return `Below the margin floor${floor}. Check the margin override acknowledgement to apply anyway.`;
  }
  if (st === 409) return 'Already changed by another agent (duplicate request). Refresh and re-check before retrying.';
  return body.error || 'Request failed.';
};

const rpReset = () => {
  rpStopPoll();
  rpState.value = 'idle'; rpJobId.value = null; rpPreview.value = null; rpApplied.value = null;
  rpReverted.value = null; rpMsg.value = ''; rpTyped.value = ''; rpOverrideAck.value = false;
  rpNeedsOverride.value = false; rpTries = 0;
};

// Phase 2 POLL: status every ~2.5s until ready | scrape_failed | cap.
const rpStartPoll = () => {
  rpStopPoll();
  rpState.value = 'polling';
  rpTries = 0;
  rpTimer = setInterval(async () => {
    if (rpTries >= RP_POLL_CAP) {
      rpStopPoll();
      rpState.value = 'timeout'; // copy lives in the template + a Retry re-polls the SAME job_id
      return;
    }
    rpTries += 1;
    try {
      const s = await post('repricefromsource', { action: 'status', job_id: String(rpJobId.value) });
      if (s.status === 'ready') {
        rpStopPoll(); rpPreview.value = s; rpState.value = 'ready';
      } else if (s.status === 'scrape_failed') {
        // FAIL-CLOSED: no write offered, no guessed-price fallback.
        rpStopPoll(); rpState.value = 'scrape_failed';
        rpMsg.value = 'Could not read a reliable price from the source. No change was made.'
          + (s.reason ? ` (${s.reason})` : '');
      } else if (s.ok === false) {
        rpStopPoll(); rpState.value = 'idle'; rpMsg.value = s.error || 'status check failed';
      } // else pending -> keep polling
    } catch (e) {
      rpStopPoll(); rpState.value = 'idle'; rpMsg.value = rpErrText(e);
    }
  }, RP_POLL_MS);
};
const rpRetryPoll = () => { if (rpJobId.value) { rpMsg.value = ''; rpStartPoll(); } };

// Phase 1 RUN: enqueue the source scrape, get job_id, start polling.
const runReprice = async () => {
  if (rpBusy.value) return;
  if (!rpTarget.value) { rpMsg.value = 'Pick a product.'; return; }
  rpReset();
  rpBusy.value = true; rpState.value = 'queueing';
  try {
    const r = await post('repricefromsource', { action: 'run', ...rpIdFields(), scope: 'global' });
    if (r.ok && r.job_id) { rpJobId.value = r.job_id; rpStartPoll(); }
    else if (r.error === 'no_source_url') {
      rpState.value = 'idle';
      rpMsg.value = 'This product has no source URL to rescrape from. Use Set store-wide price instead.';
    } else {
      rpState.value = 'idle';
      rpMsg.value = r.error === 'enqueue_failed' ? 'Could not queue the source read. Try again.' : (r.error || 'run failed');
    }
  } catch (e) { rpState.value = 'idle'; rpMsg.value = rpErrText(e); }
  finally { rpBusy.value = false; }
};

const rpModalSku = computed(() => String(rpPreview.value?.sku || rpTarget.value?.sku || ''));
const rpTypedOk = computed(() => {
  if (rpPreview.value?.requires_typed_confirm === false) return true;
  const t = rpTyped.value.trim();
  if (!t) return false;
  return t === 'CONFIRM' || (rpModalSku.value && t.toLowerCase() === rpModalSku.value.toLowerCase());
});
const rpMarginBlocked = computed(() => rpPreview.value?.margin_ok === false || rpNeedsOverride.value);
const rpCanApply = computed(() => rpTypedOk.value && (!rpMarginBlocked.value || rpOverrideAck.value));

// Phase 3 CONFIRM: gated write - the server re-reads the completed job's scraped price.
const confirmReprice = async () => {
  if (!rpCanApply.value || rpBusy.value) return;
  rpBusy.value = true; rpMsg.value = '';
  try {
    const r = await post('repricefromsource', {
      action: 'confirm', job_id: String(rpJobId.value), ...rpIdFields(), scope: 'global',
      confirm: rpTyped.value.trim() || '1',
      margin_override: rpOverrideAck.value ? '1' : '0',
    }, mkIdem());
    if (r.ok && r.applied) { rpApplied.value = r; rpState.value = 'applied'; }
    else if (r.status === 'pending') { rpMsg.value = 'The source read is not finished yet.'; rpStartPoll(); }
    else if (r.status === 'scrape_failed') {
      rpState.value = 'scrape_failed';
      rpMsg.value = 'Could not read a reliable price from the source. No change was made.';
    }
    else if (r.ok && r.preview) { rpPreview.value = r; rpMsg.value = 'Server treated this as a preview. Type the exact SKU or CONFIRM and apply again.'; }
    else rpMsg.value = r.error || 'confirm failed';
  } catch (e) { rpMsg.value = rpErrText(e); }
  finally { rpBusy.value = false; }
};

// REVERT (cross-endpoint): #53 has NO own revert - reuse the #52 setcatalogprice revert=1
// path via the confirm response's revert_hint (shared CatalogPriceWriter restores the exact
// prior price from the nlh_agent_cart_audit ledger).
const revertReprice = async () => {
  if (rpBusy.value || !rpApplied.value) return;
  const hint = rpApplied.value.revert_hint || {};
  rpBusy.value = true; rpMsg.value = '';
  try {
    const r = await post('setcatalogprice', {
      sku: String(hint.sku || rpApplied.value.sku || ''), revert: '1',
      scope: String(hint.scope || 'global'),
      confirm: rpTypedOk.value && rpTyped.value.trim() ? rpTyped.value.trim() : 'CONFIRM',
    }, mkIdem());
    if (r.ok) rpReverted.value = r;
    else rpMsg.value = r.error || 'revert failed';
  } catch (e) { rpMsg.value = rpErrText(e); }
  finally { rpBusy.value = false; }
};

// ---- 2. coupon ----
const cSku = ref(''); const cMode = ref('percent'); const cAmt = ref(''); const cMsg = ref(''); const cBusy = ref(false);
const makeCoupon = async () => {
  cMsg.value = '';
  if (!cSku.value) { cMsg.value = 'Pick a product.'; return; }
  const p = { sku: cSku.value, mode: cMode.value };
  if (cMode.value === 'fixed') p.amount_off = cAmt.value;
  if (cMode.value === 'percent') p.percent_off = cAmt.value;
  cBusy.value = true;
  try {
    const r = await post('createcoupon', p, mkIdem());
    const res = r.result || {};
    if (res.code) cMsg.value = `Coupon minted: ${res.code}`;
    else if (res.outcome === 'pending' || res.pending_id) cMsg.value = `Sent for approval (over margin floor). Pending #${res.pending_id}.`;
    else cMsg.value = res.error || r.error || 'coupon failed';
  } finally { cBusy.value = false; }
};

// ---- 3. add product from URL ----
const url = ref(''); const addMsg = ref(''); const addBusy = ref(false); const scraped = ref(null);
const scrapeUrl = async () => {
  addMsg.value = 'Scraping...'; scraped.value = null;
  if (!url.value) { addMsg.value = 'Enter a URL.'; return; }
  addBusy.value = true;
  try {
    const q = await post('addproduct', { action: 'scrape', url: url.value });
    if (q.status !== 'queued') { addMsg.value = q.error || 'could not queue'; addBusy.value = false; return; }
    let tries = 0;
    const poll = setInterval(async () => {
      if (++tries > 90) { clearInterval(poll); addMsg.value = 'Timed out.'; addBusy.value = false; return; }
      const s = await post('addproduct', { action: 'status', request_id: q.request_id });
      if (s.request_status === 'complete') {
        clearInterval(poll); addBusy.value = false;
        const pl = s.payload || {};
        if (!pl || pl.status === 'failed') { addMsg.value = 'Scrape failed: ' + (pl.reason || '?'); return; }
        scraped.value = pl; addMsg.value = `Scraped: ${pl.name || ''} (${pl.brand_name || ''}) $${pl.price || ''}`;
      } else if (s.request_status === 'error') { clearInterval(poll); addBusy.value = false; addMsg.value = 'Error: ' + (s.reason || '?'); }
    }, 3000);
  } catch (e) { addBusy.value = false; addMsg.value = 'error'; }
};
const createScraped = async () => {
  if (!scraped.value) return;
  addBusy.value = true; addMsg.value = 'Creating...';
  try {
    const r = await post('addproduct', { action: 'create', payload_json: JSON.stringify(scraped.value), image_mode: 'as_is' }, mkIdem());
    if (r.status === 'ok' || r.status === 'updated') { addMsg.value = `Product ${r.status}: ${r.sku}`; scraped.value = null; url.value = ''; }
    else if (r.status === 'duplicate') { addMsg.value = 'Already on store - use the admin panel to update/confirm.'; }
    else addMsg.value = r.reason || r.error || 'create failed';
  } finally { addBusy.value = false; }
};

// ---- 4. add to cart (#55) ----
// LIGHT flow (one customer's own reversible cart - deliberately NOT the heavy #52/#53
// typed-confirm/role-gate): pick a product (prefill from current_product, or the REUSED
// cockpit partsearch read) -> qty -> preview (mutates nothing; shows the line + the
// current cart) -> add (idempotency_key: a retry 409s, never double-adds) -> added line
// + cart summary + Remove (the removecartline companion action, same server-resolved
// quote). The BFF whitelists ONLY action/sku/product_id/qty - no customer_id/quote_id -
// and injects the conversation contact's email server-side (anti-spoof).
const { bffGetAccount } = useInteliBff();
// UX-hide only (same pattern as #52/#53): absent map = show; default true.
const cartAllowed = computed(() => {
  const aw = props.allowedWrites;
  if (!aw || typeof aw !== 'object') return true;
  return aw.addtocart !== false;
});
const ctTarget = ref(null); // {sku, product_id, name, price}
const ctQty = ref('1');
const ctQuery = ref('');
const ctResults = ref([]);
const ctSearchState = ref('idle'); // idle | searching | ready | empty | error
const ctPreview = ref(null); // {ok, preview:true, product:{sku,name,qty,price}, cart:{item_count,subtotal}}
const ctAdded = ref(null);   // {ok, added:{sku,name,qty,price}, cart:{item_count,subtotal}}
const ctRemoved = ref(null); // {ok, removed:{sku,name,qty}, cart:{item_count,subtotal}}
const ctMsg = ref('');
const ctBusy = ref(false);
let ctTimer = null;
let ctSeq = 0;
onBeforeUnmount(() => { if (ctTimer) clearTimeout(ctTimer); });

const money = v => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(2) : String(v ?? '');
};
const ctLineTotal = line => {
  const p = parseFloat(line?.price);
  const q = parseInt(line?.qty, 10) || 1;
  return Number.isFinite(p) ? (p * q).toFixed(2) : null;
};

// Error copy per the #55 contract: no_customer_cart is the EXACT Magento code.
const ctCodeText = code => {
  if (code === 'no_customer_cart') return 'This customer has no active cart yet. Ask them to start one on the store, then retry.';
  if (code === 'out_of_stock') return 'Cannot add: this product is out of stock.';
  if (code === 'product_not_found') return 'Cannot find that product on the store. Pick another result.';
  if (code === 'sku_or_product_id_required') return 'Pick a product first.';
  if (code === 'line_not_found') return 'Cannot remove: that line is no longer in the cart.';
  if (code === 'remove_failed') return 'Remove failed. Try again.';
  return code || '';
};
const ctErrText = e => {
  const st = e?.response?.status;
  const body = e?.response?.data || {};
  if (st === 409) return 'Request already sent (duplicate). Check the cart before retrying.';
  return ctCodeText(body.error) || 'Request failed.';
};

// SEARCH: reuse the existing cockpit partsearch (the BFF proxy of the public storefront
// suggest endpoint InteliSearch already uses) - debounced, keystroke-superseded.
const runCartSearch = async q => {
  const seq = (ctSeq += 1);
  ctSearchState.value = 'searching';
  try {
    const data = await bffGetAccount('partsearch', { q });
    if (seq !== ctSeq) return; // a newer keystroke superseded this one
    if (data?.ok === true) {
      const products = Array.isArray(data.products) ? data.products : [];
      const children = Array.isArray(data.child_matches) ? data.child_matches : [];
      ctResults.value = [...products, ...children].slice(0, 8);
      ctSearchState.value = ctResults.value.length ? 'ready' : 'empty';
    } else { ctResults.value = []; ctSearchState.value = 'error'; }
  } catch {
    if (seq !== ctSeq) return;
    ctResults.value = []; ctSearchState.value = 'error';
  }
};
const onCartQuery = () => {
  if (ctTimer) clearTimeout(ctTimer);
  const q = ctQuery.value.trim();
  if (q.length < 2) { ctSeq += 1; ctResults.value = []; ctSearchState.value = 'idle'; return; }
  ctTimer = setTimeout(() => runCartSearch(q), 300);
};
const pickCartResult = row => {
  ctTarget.value = { sku: row.sku, product_id: row.product_id, name: row.name, price: row.price };
  ctQuery.value = ''; ctResults.value = []; ctSearchState.value = 'idle'; ctMsg.value = '';
};
// PREFILL: one click pulls the browsed product (data.behavior.current_product) in.
const useViewedProduct = () => {
  const cp = currentProduct.value;
  if (!cp) return;
  ctTarget.value = {
    sku: cp.sku, product_id: cp.product_id, name: cp.name,
    price: cp.current_price != null ? cp.current_price : cp.price,
  };
  ctMsg.value = '';
};
const changeCartProduct = () => { ctTarget.value = null; ctPreview.value = null; ctMsg.value = ''; };
const ctIdFields = () => {
  const t = ctTarget.value || {};
  return t.product_id ? { product_id: String(t.product_id) } : { sku: String(t.sku || '') };
};
const ctQtyVal = () => String(Math.max(1, parseInt(ctQty.value, 10) || 1));
const ctReset = () => {
  ctTarget.value = null; ctPreview.value = null; ctAdded.value = null; ctRemoved.value = null;
  ctMsg.value = ''; ctQty.value = '1'; ctQuery.value = ''; ctResults.value = []; ctSearchState.value = 'idle';
};

// LIGHT confirm step 1: preview (action=preview mutates nothing) - what will be added + the current cart.
const previewCartAdd = async () => {
  if (ctBusy.value || !ctTarget.value) return;
  ctBusy.value = true; ctMsg.value = ''; ctPreview.value = null;
  try {
    const r = await post('addtocart', { action: 'preview', ...ctIdFields(), qty: ctQtyVal() });
    if (r.ok && r.preview) ctPreview.value = r;
    else ctMsg.value = ctCodeText(r.error) || 'Preview failed.';
  } catch (e) { ctMsg.value = ctErrText(e); }
  finally { ctBusy.value = false; }
};
// LIGHT confirm step 2: add. Idempotent: a retry of the same add 409s, never double-adds.
const confirmCartAdd = async () => {
  if (ctBusy.value || !ctTarget.value) return;
  ctBusy.value = true; ctMsg.value = '';
  try {
    const r = await post('addtocart', { action: 'add', ...ctIdFields(), qty: ctQtyVal() }, mkIdem());
    if (r.ok && r.added) { ctAdded.value = r; ctPreview.value = null; }
    else ctMsg.value = ctCodeText(r.error) || 'Add failed.';
  } catch (e) { ctMsg.value = ctErrText(e); }
  finally { ctBusy.value = false; }
};
// REMOVE (undo): the removecartline companion action - remove the just-added line by sku
// from the same server-resolved quote. Functions once RemoveCartLine deploys on Magento.
const removeAddedLine = async () => {
  if (ctBusy.value || !ctAdded.value) return;
  ctBusy.value = true; ctMsg.value = '';
  try {
    const r = await post('removecartline', { sku: String(ctAdded.value.added?.sku || '') }, mkIdem());
    if (r.ok) ctRemoved.value = r;
    else ctMsg.value = ctCodeText(r.error) || 'Remove failed.';
  } catch (e) { ctMsg.value = ctErrText(e); }
  finally { ctBusy.value = false; }
};

// ---- 5. disable sales (#54) ----
// STORE-WIDE, HIGH-risk - mirrors the #52 discipline exactly: preview (mutates nothing,
// passes the role-gate) -> typed-confirm modal (blast radius in words, current -> target
// state, already_off called out clearly) -> apply -> a clear one-click RE-ENABLE (revert=1
// restores the EXACT prior state from the server-side nlh_agent_cart_audit ledger).
// mode: nocart (default - is_in_stock=0 so the theme hides the add-to-cart button, the PDP
// stays visible) | disable (status=2 - the product is fully hidden). Hidden when
// allowed_writes.disablesales === false (Magento computes it role-only; unmapped agents get
// false); the BFF ROLE_NOT_ALLOWED 403 + the Magento deny-by-default role-gate stay the
// real enforcement.
const dsAllowed = computed(() => {
  const aw = props.allowedWrites;
  if (!aw || typeof aw !== 'object') return true;
  return aw.disablesales !== false;
});
const dsSku = ref(''); // '' = use the prefilled browsed product; select overrides
const dsMode = ref('nocart'); // nocart (default, softer) | disable (fully hide)
const dsTarget = computed(() => {
  if (dsSku.value) {
    const p = (props.products || []).find(x => x.sku === dsSku.value);
    return p ? { sku: p.sku, name: p.name, current_price: p.price } : { sku: dsSku.value };
  }
  return currentProduct.value;
});
const dsIdFields = () => {
  const t = dsTarget.value || {};
  return t.product_id ? { product_id: String(t.product_id) } : { sku: String(t.sku || '') };
};
const dsPreview = ref(null);  // preview payload {current_state, target_state, already_off, blast_radius, ...}
const dsApplied = ref(null);  // applied payload {prior_state, new_state, revert_hint}
const dsReverted = ref(null); // revert payload (applied + reverted:true)
const dsTyped = ref('');
const showDsModal = ref(false);
const dsMsg = ref('');
const dsBusy = ref(false);
const dsModalSku = computed(() => String(dsPreview.value?.sku || dsTarget.value?.sku || ''));
const dsModalName = computed(() => dsPreview.value?.name || dsTarget.value?.name || dsModalSku.value || 'product');
const dsTypedOk = computed(() => {
  if (dsPreview.value?.requires_typed_confirm === false) return true;
  const t = dsTyped.value.trim();
  if (!t) return false;
  return t === 'CONFIRM' || (dsModalSku.value && t.toLowerCase() === dsModalSku.value.toLowerCase());
});
// Render a sales-state snapshot ({mode, is_in_stock|status}) in agent words.
const dsStateText = st => {
  if (!st || typeof st !== 'object') return '';
  if (st.mode === 'disable' || st.status != null) {
    return Number(st.status) === 2 ? 'Disabled (hidden from the store)' : 'Enabled (visible and sellable)';
  }
  return Number(st.is_in_stock) === 1 ? 'Add-to-cart ON' : 'Add-to-cart OFF';
};
const dsCodeText = code => {
  if (!code) return '';
  if (code === 'product_not_found') return 'Cannot find that product on the store.';
  if (code === 'sku_or_product_id_required') return 'Pick a product first.';
  if (code === 'no_prior_state_to_revert') return 'Nothing to re-enable from: no prior state is recorded for this product and mode.';
  if (code === 'action_not_permitted' || code === 'ROLE_NOT_ALLOWED') return 'Not allowed: this agent account has no disable-sales role.';
  if (String(code).startsWith('save_failed')) return 'Save failed on the store. Try again.';
  return String(code);
};
const dsErrText = e => {
  const st = e?.response?.status;
  const body = e?.response?.data || {};
  if (st === 403) return 'Not allowed: this agent account has no disable-sales role.';
  if (st === 409) return 'Already changed by another agent (duplicate request). Refresh and re-check before retrying.';
  return dsCodeText(body.error) || 'Request failed.';
};
const dsReset = () => {
  dsPreview.value = null; dsApplied.value = null; dsReverted.value = null;
  dsTyped.value = ''; dsMsg.value = '';
};
// Step 1 PREVIEW: no confirm at all -> a pure read (passes the role-gate). already_off comes
// back on the preview and renders as a CLEAR message, never a silent no-op.
const previewDisable = async () => {
  if (dsBusy.value) return;
  if (!dsTarget.value) { dsMsg.value = 'Pick a product.'; return; }
  dsReset();
  dsBusy.value = true;
  try {
    const r = await post('disablesales', { ...dsIdFields(), mode: dsMode.value, scope: 'global' });
    if (r.ok && r.preview) { dsPreview.value = r; showDsModal.value = true; }
    else dsMsg.value = dsCodeText(r.error) || 'preview failed';
  } catch (e) { dsMsg.value = dsErrText(e); }
  finally { dsBusy.value = false; }
};
// Step 2 APPLY: GLOBAL write - confirm carries the TYPED value (the SKU or CONFIRM).
const applyDisable = async () => {
  if (dsBusy.value || !dsTypedOk.value) return;
  dsBusy.value = true; dsMsg.value = '';
  try {
    const r = await post('disablesales', {
      ...dsIdFields(), mode: dsMode.value, scope: 'global', confirm: dsTyped.value.trim(),
    }, mkIdem());
    if (r.ok && r.applied) dsApplied.value = r;
    else if (r.ok && r.preview) dsMsg.value = 'Server treated this as a preview. Type the exact SKU or CONFIRM and apply again.';
    else dsMsg.value = dsCodeText(r.error) || 'apply failed';
  } catch (e) { dsMsg.value = dsErrText(e); }
  finally { dsBusy.value = false; }
};
// RE-ENABLE (exact revert, lock #5): revert=1 restores the exact prior state from the
// ledger. One-click after an apply (auto CONFIRM, same as the #52 revert); from the
// already_off preview it stays typed-confirm gated (an independent mutation).
const revertDisable = async () => {
  if (dsBusy.value) return;
  dsBusy.value = true; dsMsg.value = '';
  try {
    const r = await post('disablesales', {
      ...dsIdFields(), mode: dsMode.value, scope: 'global', revert: '1',
      confirm: dsTypedOk.value && dsTyped.value.trim() ? dsTyped.value.trim() : 'CONFIRM',
    }, mkIdem());
    if (r.ok && r.applied) dsReverted.value = r;
    else if (r.ok && r.preview) dsMsg.value = 'Server treated this as a preview. Type the exact SKU or CONFIRM and re-enable again.';
    else dsMsg.value = dsCodeText(r.error) || 'revert failed';
  } catch (e) { dsMsg.value = dsErrText(e); }
  finally { dsBusy.value = false; }
};
const closeDsModal = () => {
  showDsModal.value = false;
  if (dsApplied.value || dsReverted.value) dsReset();
};
</script>

<template>
  <div v-if="ready" class="ne-shop-actions px-4 py-3 border-b border-n-weak">
    <div class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 mb-1.5">Agent Actions</div>

    <!-- 1. store-wide price (#52): hidden when allowed_writes.setcatalogprice is false (UX;
         the BFF/Magento role-gate is the real enforcement) -->
    <button v-if="priceAllowed" @click="toggle('price')" class="ne-act">Set store-wide price</button>
    <div v-if="priceAllowed && open==='price'" class="ne-body">
      <div v-if="currentProduct && !priceSku" class="ne-note">
        Viewing now: <strong>{{ currentProduct.name || currentProduct.sku }}</strong>
        <span v-if="currentProduct.sku"> ({{ currentProduct.sku }})</span>
        <span v-if="currentProduct.current_price != null"> - current ${{ currentProduct.current_price }}</span>
      </div>
      <select v-if="products.length" v-model="priceSku">
        <option value="">{{ currentProduct ? 'Use the viewed product (above)' : 'Select product...' }}</option>
        <option v-for="p in products" :key="p.sku" :value="p.sku">{{ p.name || p.sku }} ({{ p.sku }})</option>
      </select>
      <input v-model="newPrice" type="number" step="0.01" placeholder="New store-wide price $" />
      <button :disabled="priceBusy || !priceTarget" @click="previewPrice">Preview store-wide change</button>
      <div v-if="priceMsg && !showPriceModal" class="ne-msg">{{ priceMsg }}</div>
    </div>

    <!-- #52 confirm modal: blast-radius in words, old -> new, margin status, typed confirm,
         then the one-click exact REVERT after an applied write -->
    <div v-if="showPriceModal && pricePreview" class="ne-modal-backdrop" @click.self="closePriceModal">
      <div class="ne-modal">
        <div class="ne-modal-title">Set store-wide price</div>
        <template v-if="!priceApplied">
          <div class="ne-blast">This changes the price for EVERY customer, on all store views.</div>
          <div class="ne-line"><strong>{{ modalName }}</strong><span v-if="modalSku"> ({{ modalSku }})</span></div>
          <div class="ne-line ne-delta">
            ${{ pricePreview.current_price }} -> ${{ pricePreview.new_price }}
            <span v-if="pricePreview.delta_pct != null">({{ pricePreview.delta_pct }}%)</span>
          </div>
          <div class="ne-line" :class="marginBlocked ? 'ne-bad' : 'ne-good'">
            <template v-if="marginBlocked">
              BELOW margin floor (${{ pricePreview.margin_floor }})<template v-if="pricePreview.margin_reason"> - {{ pricePreview.margin_reason }}</template>
            </template>
            <template v-else>
              Margin OK<template v-if="pricePreview.margin_floor > 0"> (floor ${{ pricePreview.margin_floor }})</template>
            </template>
          </div>
          <label v-if="marginBlocked" class="ne-ack">
            <input v-model="overrideAck" type="checkbox" />
            Apply below the margin floor anyway (margin override).
          </label>
          <label class="ne-typed">
            Type the SKU<template v-if="modalSku"> ({{ modalSku }})</template> or CONFIRM to enable Apply:
            <input v-model="typedConfirm" type="text" autocomplete="off" :placeholder="modalSku || 'CONFIRM'" />
          </label>
          <div class="ne-modal-actions">
            <button :disabled="priceBusy" @click="closePriceModal">Cancel</button>
            <button class="ne-danger" :disabled="priceBusy || !canApplyPrice" @click="applyStorewide">Apply store-wide</button>
          </div>
        </template>
        <template v-else>
          <div class="ne-line ne-good">Applied: ${{ priceApplied.current_price }} -> ${{ priceApplied.new_price }} for every customer, all store views.</div>
          <div v-if="priceReverted" class="ne-line ne-good">Reverted: the prior price is restored (${{ priceReverted.new_price != null ? priceReverted.new_price : priceApplied.current_price }}).</div>
          <div class="ne-modal-actions">
            <button v-if="!priceReverted" class="ne-danger" :disabled="priceBusy" @click="revertStorewide">Revert to ${{ priceApplied.current_price }}</button>
            <button :disabled="priceBusy" @click="closePriceModal">Close</button>
          </div>
        </template>
        <div v-if="priceMsg" class="ne-msg">{{ priceMsg }}</div>
      </div>
    </div>

    <!-- 1b. reprice from source (#53): hidden when allowed_writes.repricefromsource is false
         (UX; the BFF/Magento role-gate is the real enforcement). ASYNC: run -> poll (2.5s,
         cap ~60s -> Retry) -> ready preview -> typed confirm -> applied + cross-endpoint revert. -->
    <button v-if="repriceAllowed" @click="toggle('reprice')" class="ne-act">Reprice from source</button>
    <div v-if="repriceAllowed && open==='reprice'" class="ne-body">
      <div v-if="currentProduct && !rpSku" class="ne-note">
        Viewing now: <strong>{{ currentProduct.name || currentProduct.sku }}</strong>
        <span v-if="currentProduct.sku"> ({{ currentProduct.sku }})</span>
        <span v-if="currentProduct.current_price != null"> - current ${{ currentProduct.current_price }}</span>
      </div>
      <select v-if="products.length && (rpState==='idle' || rpState==='queueing')" v-model="rpSku" :disabled="rpBusy">
        <option value="">{{ currentProduct ? 'Use the viewed product (above)' : 'Select product...' }}</option>
        <option v-for="p in products" :key="p.sku" :value="p.sku">{{ p.name || p.sku }} ({{ p.sku }})</option>
      </select>

      <!-- idle: kick off the source read -->
      <button v-if="rpState==='idle'" :disabled="rpBusy || !rpTarget" @click="runReprice">Read the source price</button>

      <!-- queueing / polling spinner -->
      <div v-if="rpState==='queueing' || rpState==='polling'" class="ne-line">
        <span class="ne-spin"></span>
        {{ rpState==='queueing' ? 'Queueing the source read...' : 'Still reading the source...' }}
      </div>

      <!-- poll cap reached: degrade gracefully, never hang -->
      <template v-if="rpState==='timeout'">
        <div class="ne-line">The source is still being read. Try again in a moment.</div>
        <button :disabled="rpBusy" @click="rpRetryPoll">Retry</button>
        <button :disabled="rpBusy" @click="rpReset">Cancel</button>
      </template>

      <!-- scrape failed: FAIL-CLOSED, no write offered -->
      <template v-if="rpState==='scrape_failed'">
        <div class="ne-line ne-bad">{{ rpMsg }}</div>
        <button :disabled="rpBusy" @click="rpReset">Start over</button>
      </template>

      <!-- ready: preview current -> scraped + margin + blast radius, typed confirm -->
      <template v-if="rpState==='ready' && rpPreview">
        <div class="ne-blast">{{ rpPreview.blast_radius || 'This changes the price for EVERY customer, on all store views.' }}</div>
        <div class="ne-line"><strong>{{ rpPreview.name || rpModalSku }}</strong><span v-if="rpModalSku"> ({{ rpModalSku }})</span></div>
        <div class="ne-line ne-delta">
          Source price: ${{ rpPreview.current_price }} -> ${{ rpPreview.scraped_price }}
          <span v-if="rpPreview.delta_pct != null">({{ rpPreview.delta_pct }}%)</span>
        </div>
        <div class="ne-line" :class="rpMarginBlocked ? 'ne-bad' : 'ne-good'">
          <template v-if="rpMarginBlocked">
            BELOW margin floor (${{ rpPreview.margin_floor }})<template v-if="rpPreview.margin_reason"> - {{ rpPreview.margin_reason }}</template>
          </template>
          <template v-else>
            Margin OK<template v-if="rpPreview.margin_floor > 0"> (floor ${{ rpPreview.margin_floor }})</template>
          </template>
        </div>
        <label v-if="rpMarginBlocked" class="ne-ack">
          <input v-model="rpOverrideAck" type="checkbox" />
          Apply below the margin floor anyway (margin override).
        </label>
        <label v-if="rpPreview.requires_typed_confirm !== false" class="ne-typed">
          Type the SKU<template v-if="rpModalSku"> ({{ rpModalSku }})</template> or CONFIRM to enable Apply:
          <input v-model="rpTyped" type="text" autocomplete="off" :placeholder="rpPreview.typed_confirm_hint || rpModalSku || 'CONFIRM'" />
        </label>
        <div class="ne-modal-actions">
          <button :disabled="rpBusy" @click="rpReset">Cancel</button>
          <button class="ne-danger" :disabled="rpBusy || !rpCanApply" @click="confirmReprice">Apply the source price</button>
        </div>
      </template>

      <!-- applied: before -> after + the cross-endpoint (#52 revert=1) exact revert -->
      <template v-if="rpState==='applied' && rpApplied">
        <div class="ne-line ne-good">Applied: ${{ rpApplied.before }} -> ${{ rpApplied.after }} for every customer, all store views.</div>
        <div v-if="rpReverted" class="ne-line ne-good">Reverted: the prior price is restored (${{ rpReverted.new_price != null ? rpReverted.new_price : rpApplied.before }}).</div>
        <button v-if="!rpReverted" class="ne-danger" :disabled="rpBusy" @click="revertReprice">Revert to ${{ rpApplied.revert_hint && rpApplied.revert_hint.prior_price != null ? rpApplied.revert_hint.prior_price : rpApplied.before }}</button>
        <button :disabled="rpBusy" @click="rpReset">Done</button>
      </template>

      <div v-if="rpMsg && rpState!=='scrape_failed'" class="ne-msg">{{ rpMsg }}</div>
    </div>

    <!-- 1c. disable sales (#54): hidden when allowed_writes.disablesales is false (UX only;
         the BFF ROLE_NOT_ALLOWED 403 + the Magento deny-by-default role-gate are the real
         enforcement). STORE-WIDE like #52: preview -> typed-confirm modal -> apply -> a
         clear one-click RE-ENABLE (exact revert). Modes: nocart (default) | disable. -->
    <button v-if="dsAllowed" @click="toggle('disable')" class="ne-act">Disable add-to-cart</button>
    <div v-if="dsAllowed && open==='disable'" class="ne-body">
      <div v-if="currentProduct && !dsSku" class="ne-note">
        Viewing now: <strong>{{ currentProduct.name || currentProduct.sku }}</strong>
        <span v-if="currentProduct.sku"> ({{ currentProduct.sku }})</span>
      </div>
      <select v-if="products.length" v-model="dsSku">
        <option value="">{{ currentProduct ? 'Use the viewed product (above)' : 'Select product...' }}</option>
        <option v-for="p in products" :key="p.sku" :value="p.sku">{{ p.name || p.sku }} ({{ p.sku }})</option>
      </select>
      <select v-model="dsMode">
        <option value="nocart">Hide add-to-cart (product page stays visible)</option>
        <option value="disable">Fully hide the product</option>
      </select>
      <button :disabled="dsBusy || !dsTarget" @click="previewDisable">Preview store-wide change</button>
      <div v-if="dsMsg && !showDsModal" class="ne-msg">{{ dsMsg }}</div>
    </div>

    <!-- #54 confirm modal: blast radius in words, current -> target state, the CLEAR
         already_off message (never a silent no-op), typed confirm, then the one-click
         RE-ENABLE (exact revert) after an applied write -->
    <div v-if="showDsModal && dsPreview" class="ne-modal-backdrop" @click.self="closeDsModal">
      <div class="ne-modal">
        <div class="ne-modal-title">{{ dsMode === 'disable' ? 'Disable product' : 'Disable add-to-cart' }}</div>
        <template v-if="!dsApplied && !dsReverted">
          <div class="ne-blast">{{ dsPreview.blast_radius || 'This affects EVERY customer, on all store views.' }}</div>
          <div class="ne-line"><strong>{{ dsModalName }}</strong><span v-if="dsModalSku"> ({{ dsModalSku }})</span></div>
          <template v-if="dsPreview.already_off">
            <div class="ne-line ne-bad">
              {{ dsMode === 'disable' ? 'This product is already disabled.' : "This product's add-to-cart is already disabled." }}
            </div>
            <div class="ne-line">Current state: {{ dsStateText(dsPreview.current_state) }}. Re-enable restores the exact prior state.</div>
            <label class="ne-typed">
              Type the SKU<template v-if="dsModalSku"> ({{ dsModalSku }})</template> or CONFIRM to enable Re-enable:
              <input v-model="dsTyped" type="text" autocomplete="off" :placeholder="dsModalSku || 'CONFIRM'" />
            </label>
            <div class="ne-modal-actions">
              <button :disabled="dsBusy" @click="closeDsModal">Close</button>
              <button class="ne-danger" :disabled="dsBusy || !dsTypedOk" @click="revertDisable">Re-enable sales</button>
            </div>
          </template>
          <template v-else>
            <div class="ne-line ne-delta">{{ dsStateText(dsPreview.current_state) }} -> {{ dsStateText(dsPreview.target_state) }}</div>
            <label class="ne-typed">
              Type the SKU<template v-if="dsModalSku"> ({{ dsModalSku }})</template> or CONFIRM to enable Apply:
              <input v-model="dsTyped" type="text" autocomplete="off" :placeholder="dsPreview.typed_confirm_hint || dsModalSku || 'CONFIRM'" />
            </label>
            <div class="ne-modal-actions">
              <button :disabled="dsBusy" @click="closeDsModal">Cancel</button>
              <button class="ne-danger" :disabled="dsBusy || !dsTypedOk" @click="applyDisable">
                {{ dsMode === 'disable' ? 'Disable store-wide' : 'Turn off add-to-cart store-wide' }}
              </button>
            </div>
          </template>
        </template>
        <template v-else>
          <div v-if="dsApplied" class="ne-line ne-good">
            Applied: {{ dsStateText(dsApplied.prior_state) }} -> {{ dsStateText(dsApplied.new_state) }} for every customer, all store views.
          </div>
          <div v-if="dsReverted" class="ne-line ne-good">
            Re-enabled: the exact prior state is restored ({{ dsStateText(dsReverted.new_state) }}).
          </div>
          <div class="ne-modal-actions">
            <button v-if="dsApplied && !dsReverted" class="ne-danger" :disabled="dsBusy" @click="revertDisable">Re-enable sales</button>
            <button :disabled="dsBusy" @click="closeDsModal">Close</button>
          </div>
        </template>
        <div v-if="dsMsg" class="ne-msg">{{ dsMsg }}</div>
      </div>
    </div>

    <!-- 2. coupon -->
    <button @click="toggle('coupon')" class="ne-act">Make coupon</button>
    <div v-if="open==='coupon'" class="ne-body">
      <select v-model="cSku"><option value="">Select product...</option>
        <option v-for="p in products" :key="p.sku" :value="p.sku">{{ p.name || p.sku }} ({{ p.sku }})</option></select>
      <select v-model="cMode"><option value="percent">Percent %</option><option value="fixed">Fixed $</option><option value="auto">Auto (margin-max)</option></select>
      <input v-if="cMode!=='auto'" v-model="cAmt" type="number" step="0.01" :placeholder="cMode==='percent' ? '% off' : '$ off'" />
      <button :disabled="cBusy" @click="makeCoupon">Create coupon</button>
      <div v-if="cMsg" class="ne-msg">{{ cMsg }}</div>
    </div>

    <!-- 3. add product -->
    <button @click="toggle('add')" class="ne-act">Add product from URL</button>
    <div v-if="open==='add'" class="ne-body">
      <input v-model="url" type="url" placeholder="https://manufacturer.com/product" />
      <button :disabled="addBusy" @click="scrapeUrl">Scrape</button>
      <button v-if="scraped" :disabled="addBusy" @click="createScraped">Add to store</button>
      <div v-if="addMsg" class="ne-msg">{{ addMsg }}</div>
    </div>

    <!-- 4. add to cart (#55): the customer's own cart, LIGHT confirm (preview -> add), no
         typed-confirm. Hidden when allowed_writes.addtocart is false (absent map = show);
         the BFF + Magento gate stay the real enforcement. Search REUSES the cockpit
         partsearch; Remove wires to the removecartline companion action. -->
    <button v-if="cartAllowed" @click="toggle('cart')" class="ne-act">Add to cart</button>
    <div v-if="cartAllowed && open==='cart'" class="ne-body">
      <!-- removed: the undo landed -->
      <template v-if="ctRemoved">
        <div class="ne-line ne-good">
          Removed {{ (ctRemoved.removed && (ctRemoved.removed.name || ctRemoved.removed.sku)) || 'the line' }} from the cart.
          <template v-if="ctRemoved.cart"> Cart now: {{ ctRemoved.cart.item_count }} items, ${{ money(ctRemoved.cart.subtotal) }}.</template>
        </div>
        <button :disabled="ctBusy" @click="ctReset">Done</button>
      </template>

      <!-- added: the line + the cart's new totals + Remove (undo) -->
      <template v-else-if="ctAdded">
        <div class="ne-line ne-good">
          Added <strong>{{ ctAdded.added.name || ctAdded.added.sku }}</strong> ({{ ctAdded.added.sku }})
          x {{ ctAdded.added.qty }} at ${{ money(ctAdded.added.price) }} each<template v-if="ctLineTotal(ctAdded.added)">, line ${{ ctLineTotal(ctAdded.added) }}</template>.
        </div>
        <div v-if="ctAdded.cart" class="ne-line">Cart now: {{ ctAdded.cart.item_count }} items, ${{ money(ctAdded.cart.subtotal) }}.</div>
        <button class="ne-danger" :disabled="ctBusy" @click="removeAddedLine">Remove</button>
        <button :disabled="ctBusy" @click="ctReset">Done</button>
      </template>

      <template v-else>
        <!-- PREFILL: one-click add of the part they're viewing (the common case) -->
        <div v-if="currentProduct && !ctTarget" class="ne-note">
          Viewing now: <strong>{{ currentProduct.name || currentProduct.sku }}</strong>
          <span v-if="currentProduct.sku"> ({{ currentProduct.sku }})</span>
          <span v-if="currentProduct.current_price != null"> - ${{ money(currentProduct.current_price) }}</span>
          <button :disabled="ctBusy" @click="useViewedProduct">Add the part they're viewing</button>
        </div>

        <!-- SEARCH path: the reused cockpit partsearch (type -> results -> pick) -->
        <template v-if="!ctTarget">
          <input v-model="ctQuery" type="text" placeholder="Search the catalog (name or SKU)..." @input="onCartQuery" />
          <div v-if="ctSearchState==='searching'" class="ne-line"><span class="ne-spin"></span> Searching the catalog...</div>
          <div v-if="ctSearchState==='error'" class="ne-msg">Could not search the catalog. Type again to retry.</div>
          <div v-if="ctSearchState==='empty'" class="ne-msg">No catalog matches. Try another name or SKU.</div>
          <template v-if="ctSearchState==='ready'">
            <button v-for="row in ctResults" :key="'ct' + (row.product_id || row.sku)" class="ne-result" @click="pickCartResult(row)">
              <span class="ne-result-name">{{ row.name || row.sku }}</span>
              <span class="ne-result-meta">{{ row.sku }}<template v-if="row.price != null"> - ${{ money(row.price) }}</template></span>
            </button>
          </template>
        </template>

        <!-- picked: qty + LIGHT confirm (preview first, then add) -->
        <template v-if="ctTarget">
          <div class="ne-note">
            <strong>{{ ctTarget.name || ctTarget.sku }}</strong><span v-if="ctTarget.sku"> ({{ ctTarget.sku }})</span>
            <span v-if="ctTarget.price != null"> - ${{ money(ctTarget.price) }}</span>
            <button :disabled="ctBusy" @click="changeCartProduct">Change product</button>
          </div>
          <label class="ne-qty">Qty
            <input v-model="ctQty" type="number" min="1" step="1" />
          </label>
          <button v-if="!ctPreview" :disabled="ctBusy" @click="previewCartAdd">{{ ctBusy ? 'Checking...' : 'Preview add' }}</button>
          <template v-if="ctPreview">
            <div class="ne-line">
              Will add: <strong>{{ ctPreview.product.name || ctPreview.product.sku }}</strong> ({{ ctPreview.product.sku }})
              x {{ ctPreview.product.qty }} at ${{ money(ctPreview.product.price) }} each<template v-if="ctLineTotal(ctPreview.product)">, line ${{ ctLineTotal(ctPreview.product) }}</template>.
            </div>
            <div v-if="ctPreview.cart" class="ne-line">Cart before add: {{ ctPreview.cart.item_count }} items, ${{ money(ctPreview.cart.subtotal) }}.</div>
            <button :disabled="ctBusy" @click="confirmCartAdd">{{ ctBusy ? 'Adding...' : 'Add to cart' }}</button>
            <button :disabled="ctBusy" @click="ctPreview = null">Cancel</button>
          </template>
        </template>
      </template>

      <div v-if="ctMsg" class="ne-msg">{{ ctMsg }}</div>
    </div>
  </div>
</template>

<style scoped>
/* Quiet secondary actions, one card language, n-slate neutral. Only the margin-override (destructive)
   carries a red hint (inline). Nothing competes with the LIVE amber accent. */
.ne-act { display:block; width:100%; text-align:left; font-size:12px; padding:5px 8px; margin:3px 0; border:1px solid rgba(120,130,150,0.28); border-radius:6px; background:transparent; color:inherit; cursor:pointer; transition:background-color .12s; }
.ne-act:hover { background:rgba(120,130,150,0.08); }
.ne-body { padding:6px 8px; background:rgba(120,130,150,0.05); border-radius:6px; margin-bottom:4px; }
.ne-body select, .ne-body input { display:block; width:100%; margin:3px 0; font-size:12px; padding:4px 6px; border:1px solid rgba(120,130,150,0.28); border-radius:5px; background:transparent; color:inherit; }
.ne-body button { font-size:12px; padding:4px 10px; margin:3px 4px 0 0; border:1px solid rgba(120,130,150,0.35); border-radius:5px; background:transparent; color:inherit; cursor:pointer; transition:background-color .12s; }
.ne-body button:hover { background:rgba(120,130,150,0.1); }
.ne-note { font-size:11px; margin:4px 0; padding:5px; background:rgba(120,130,150,0.06); border:1px solid rgba(120,130,150,0.18); border-radius:5px; }
.ne-msg { font-size:11px; opacity:0.85; margin-top:4px; }
/* #53 reprice-from-source: inline destructive button + the async poll spinner */
.ne-body button.ne-danger { background:#a01818; border-color:#a01818; color:#fff; }
.ne-body button.ne-danger:disabled { background:rgba(160,24,24,0.5); border-color:transparent; }
.ne-spin { display:inline-block; width:10px; height:10px; border:2px solid rgba(120,130,150,0.35); border-top-color:currentColor; border-radius:50%; animation:ne-rot 0.8s linear infinite; vertical-align:-1px; margin-right:4px; }
@keyframes ne-rot { to { transform:rotate(360deg); } }
/* #52 store-wide price confirm modal. The blast-radius banner is intentionally blunt and
   the Apply button is the destructive accent; everything else stays quiet n-slate. */
.ne-modal-backdrop { position:fixed; inset:0; z-index:9999; background:rgba(15,20,30,0.55); display:flex; align-items:center; justify-content:center; }
.ne-modal { width:min(420px, calc(100vw - 32px)); background:var(--white, #fff); color:#1f2733; border:1px solid rgba(120,130,150,0.35); border-radius:10px; padding:14px 16px; box-shadow:0 12px 40px rgba(0,0,0,0.35); font-size:12px; }
.ne-modal-title { font-size:13px; font-weight:700; margin-bottom:8px; }
.ne-blast { font-weight:700; color:#8a1f1f; background:rgba(190,40,40,0.08); border:1px solid rgba(190,40,40,0.35); border-radius:6px; padding:7px 9px; margin-bottom:8px; }
.ne-line { margin:4px 0; }
.ne-delta { font-size:13px; font-weight:600; }
.ne-good { color:#1e7a34; }
.ne-bad { color:#a01818; font-weight:600; }
.ne-ack { display:flex; gap:6px; align-items:flex-start; margin:6px 0; color:#a01818; }
.ne-ack input { margin-top:2px; }
.ne-typed { display:block; margin:8px 0 4px; }
.ne-typed input { display:block; width:100%; margin-top:3px; font-size:12px; padding:5px 7px; border:1px solid rgba(120,130,150,0.4); border-radius:5px; }
.ne-modal-actions { display:flex; justify-content:flex-end; gap:8px; margin-top:10px; }
.ne-modal-actions button { font-size:12px; padding:5px 12px; border:1px solid rgba(120,130,150,0.4); border-radius:5px; background:transparent; cursor:pointer; }
.ne-modal-actions button:disabled { opacity:0.5; cursor:not-allowed; }
.ne-modal-actions .ne-danger { background:#a01818; border-color:#a01818; color:#fff; }
.ne-modal-actions .ne-danger:disabled { background:rgba(160,24,24,0.5); border-color:transparent; }
/* #55 add-to-cart: quiet partsearch result rows + the inline qty field. Same n-slate
   language as the rest; nothing competes with the LIVE amber accent. */
.ne-body button.ne-result { display:block; width:100%; text-align:left; font-size:11px; padding:4px 6px; margin:2px 0; border:1px solid rgba(120,130,150,0.22); border-radius:5px; background:transparent; color:inherit; cursor:pointer; }
.ne-body button.ne-result:hover { background:rgba(120,130,150,0.1); }
.ne-result-name { display:block; font-weight:600; }
.ne-result-meta { display:block; opacity:0.75; }
.ne-qty { display:flex; align-items:center; gap:6px; font-size:12px; margin:3px 0; }
.ne-qty input { width:70px; display:inline-block; }
.ne-note button { font-size:11px; padding:3px 8px; margin:4px 0 0; display:block; }
</style>
